import React, { useEffect, useMemo, useState } from "react";

const ORGANIZATION_TEAM = "organization_team";
const ALL_LOANS = "all_loans";
const SELECTED_LOANS = "selected_loans";

/**
 * Lifecycle grouping for the stage list. Ids are the canonical LendingPad
 * status ids (source of truth: backend `lendingPadSyncPolicy.js`); names always
 * come from the backend projection. Any stage the backend exposes that is not
 * listed here is surfaced under "Other stages" so nothing is hidden.
 */
const STAGE_GROUPS = [
  { title: "CRM / Pre-application", ids: [1, 27] },
  { title: "Application / Pipeline", ids: [2, 3, 4, 21, 32] },
  { title: "Processing / Underwriting", ids: [33, 22, 23, 30, 31, 24, 25, 26, 28] },
  { title: "Closing / Funded / Post-closing", ids: [12, 13, 14, 15, 16, 17] },
  { title: "Terminal", ids: [18, 19, 20, 29] },
];

/**
 * Operator-facing copy per scope. The backend projection decides which scopes
 * exist; an unrecognized one still renders, labelled by its id, so a scope added
 * server-side is never silently missing from this screen.
 */
const SCOPE_COPY = {
  [ORGANIZATION_TEAM]: {
    title: "Only loans with one of this organization's people on them",
    description:
      "Someone in People must be on the loan's LendingPad team. Everyone else's loans are skipped.",
  },
  [ALL_LOANS]: {
    title: "Every loan in the imported stages",
    description:
      "Whoever is on it. For customers who run the whole LendingPad company inside one Addy organization.",
  },
  [SELECTED_LOANS]: {
    title: "Only the selected loans",
    description:
      "For pilots: only the LendingPad loans listed under Selected loans, whatever their stage or team. Every other LendingPad loan is skipped and hidden. Loans from other systems and loans created in Addy are not affected.",
  },
};


const parseLoanNumbers = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,;]+/)
        .map((number) => number.trim())
        .filter(Boolean),
    ),
  );

const describeAlreadySelected = (numbers, singular, plural) =>
  `${numbers.join(", ")} ${numbers.length === 1 ? singular : plural} already selected`;

const areSameSet = (a, b) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value));

const toSortedList = (values) => Array.from(values).sort((a, b) => a - b);

const MAX_IGNORED_FOLDERS = 50;
const MAX_FOLDER_NAME_LENGTH = 200;

/**
 * "Import policy": the organization-level rule for which LendingPad loans reach
 * Addy. Two decisions, one page, because they restrict each other — the scope
 * only decides among the loans the stages let through.
 *
 * Leads with the rule in force as one sentence, so an operator opening the tab
 * to read the answer gets it before the controls. Each decision keeps its own
 * save: widening the scope is confirmed by the panel, enabling a stage
 * backfills, and a single button for both would have to explain two
 * consequences at once.
 *
 * Deliberately does NOT decide what any person can open — that is per person,
 * in People, and the copy says so.
 *
 * Ignored folders are matched against LendingPad's folder name exactly, capital
 * letters and spaces included, and only affect documents imported from now on.
 */
export function LendingPadImportPolicyTab({
  syncStages,
  importScope,
  loansInAddy,
  disabled,
  savingScope,
  savingStages,
  onSaveScope,
  onSaveStages,
  onOpenPeople,
  ignoredDocumentFolders,
  savingFolders,
  onSaveFolders,
  selectedLoans,
  savingSelectedLoans,
  onAddSelectedLoan,
  onRemoveSelectedLoan,
}) {
  const savedScope = importScope?.scope || ORGANIZATION_TEAM;
  const availableScopes = importScope?.availableScopes?.length
    ? importScope.availableScopes
    : [ORGANIZATION_TEAM, ALL_LOANS];
  const [scope, setScope] = useState(savedScope);

  // Re-sync the draft whenever the persisted scope changes (after save + reload).
  useEffect(() => {
    setScope(savedScope);
  }, [savedScope]);

  const availableStages = useMemo(
    () => syncStages?.availableStages || [],
    [syncStages],
  );
  const defaultStatusIds = useMemo(
    () => syncStages?.defaultStatusIds || [],
    [syncStages],
  );
  const selectedStatusIds = useMemo(
    () => syncStages?.selectedStatusIds || [],
    [syncStages],
  );
  const savedKey = useMemo(
    () => toSortedList(new Set(selectedStatusIds)).join(","),
    [selectedStatusIds],
  );
  const savedSet = useMemo(() => new Set(selectedStatusIds), [selectedStatusIds]);
  const defaultSet = useMemo(() => new Set(defaultStatusIds), [defaultStatusIds]);
  const [selected, setSelected] = useState(() => new Set(selectedStatusIds));
  const [openGroups, setOpenGroups] = useState(() => new Set());

  const savedFolders = useMemo(() => ignoredDocumentFolders || [], [ignoredDocumentFolders]);
  const savedFoldersKey = JSON.stringify(savedFolders);
  const [folders, setFolders] = useState(() => [...savedFolders]);
  const [folderName, setFolderName] = useState("");
  const [folderError, setFolderError] = useState("");

  const loans = selectedLoans?.loans || [];
  const [loanNumber, setLoanNumber] = useState("");
  const [loanError, setLoanError] = useState("");

  useEffect(() => {
    setFolders([...savedFolders]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFoldersKey]);

  useEffect(() => {
    setSelected(new Set(selectedStatusIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const groups = useMemo(() => {
    const nameById = new Map(availableStages.map((stage) => [stage.id, stage.name]));
    const groupedIds = new Set(STAGE_GROUPS.flatMap((group) => group.ids));

    const lifecycleGroups = STAGE_GROUPS.map((group) => ({
      title: group.title,
      stages: group.ids
        .filter((id) => nameById.has(id))
        .map((id) => ({ id, name: nameById.get(id) })),
    })).filter((group) => group.stages.length > 0);

    const ungrouped = availableStages.filter((stage) => !groupedIds.has(stage.id));
    if (ungrouped.length > 0) {
      lifecycleGroups.push({ title: "Other stages", stages: ungrouped });
    }

    return lifecycleGroups;
  }, [availableStages]);

  const isEveryLoan = savedScope === ALL_LOANS;
  const isSelectedOnly = savedScope === SELECTED_LOANS;
  const isScopeChanged = scope !== savedScope;
  const isEnteringSelected = isScopeChanged && scope === SELECTED_LOANS;
  const isSelectedChosen = scope === SELECTED_LOANS;
  const isLeavingSelected = isScopeChanged && isSelectedOnly;
  const isWidening = savedScope === ORGANIZATION_TEAM && scope === ALL_LOANS;
  const isNarrowing = savedScope === ALL_LOANS && scope === ORGANIZATION_TEAM;

  const isStagesChanged = !areSameSet(selected, savedSet);
  const isEqualToDefault = areSameSet(selected, defaultSet);
  const addedCount = Array.from(selected).filter((id) => !savedSet.has(id)).length;
  const removedCount = Array.from(savedSet).filter((id) => !selected.has(id)).length;
  const stageCount = savedSet.size;
  const totalCount = availableStages.length;

  const isFoldersChanged =
    folders.length !== savedFolders.length ||
    folders.some((name) => !savedFolders.includes(name));
  const isFoldersDisabled = disabled || savingFolders;
  const isLoansDisabled = disabled || savingSelectedLoans;

  const toggleStage = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const setGroupSelection = (ids, isOn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (isOn ? next.add(id) : next.delete(id)));

      return next;
    });
  };

  const toggleGroup = (title) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }

      return next;
    });
  };

  const handleSaveScope = () => {
    if (disabled || savingScope || !isScopeChanged) {
      return;
    }

    onSaveScope(scope);
  };

  const handleAddFolder = (event) => {
    event.preventDefault();
    if (isFoldersDisabled || folderName.trim() === "") {
      return;
    }

    if (folders.includes(folderName)) {
      setFolderError(`"${folderName}" is already on the list`);

      return;
    }

    if (folders.length >= MAX_IGNORED_FOLDERS) {
      setFolderError(`At most ${MAX_IGNORED_FOLDERS} folders can be ignored`);

      return;
    }

    setFolders((prev) => [...prev, folderName]);
    setFolderName("");
  };

  const handleAddLoan = async (event) => {
    event.preventDefault();
    const requested = parseLoanNumbers(loanNumber);
    if (isLoansDisabled || requested.length === 0) {
      return;
    }

    const alreadySelected = requested.filter((number) =>
      loans.some((loan) => loan.loanNumber === number),
    );
    const toAdd = requested.filter((number) => !alreadySelected.includes(number));
    if (toAdd.length === 0) {
      setLoanError(describeAlreadySelected(alreadySelected, "is", "are"));

      return;
    }

    const failed = [];
    for (const number of toAdd) {
      const isAdded = await onAddSelectedLoan(number);
      if (!isAdded) {
        failed.push(number);
      }
    }

    setLoanNumber(failed.join(" "));
    if (alreadySelected.length > 0) {
      setLoanError(describeAlreadySelected(alreadySelected, "was", "were"));
    }
  };

  const handleSaveFolders = () => {
    if (isFoldersDisabled || !isFoldersChanged) {
      return;
    }

    onSaveFolders(folders.length > 0 ? folders : null);
  };

  const handleSaveStages = () => {
    if (disabled || savingStages || !isStagesChanged || selected.size === 0) {
      return;
    }

    onSaveStages(isEqualToDefault ? null : toSortedList(selected));
  };

  return (
    <div className="integrations-policy">
      <div className={`integrations-policy-rule ${isEveryLoan ? "is-broad" : ""}`}>
        <span className="integrations-policy-mark" aria-hidden="true">
          {isEveryLoan ? "◎" : "◍"}
        </span>
        <p>
          Addy imports{" "}
          <b>
            {isSelectedOnly
              ? `only the ${loans.length} selected LendingPad ${loans.length === 1 ? "loan" : "loans"}`
              : isEveryLoan
                ? "every loan"
                : "loans that have at least one of this organization's people on them"}
          </b>
          {isSelectedOnly ? (
            ", whatever their stage or team"
          ) : totalCount > 0 ? (
            <>
              , in{" "}
              <b>
                {stageCount} of {totalCount} stages
              </b>
            </>
          ) : null}
          .
          {savedFolders.length > 0 ? (
            <>
              {" "}
              It skips documents in <b>{savedFolders.length}</b> ignored{" "}
              {savedFolders.length === 1 ? "folder" : "folders"}.
            </>
          ) : null}
          {typeof loansInAddy === "number" && !isSelectedOnly ? (
            <>
              {" "}
              That is <b>{loansInAddy.toLocaleString()}</b>{" "}
              {loansInAddy === 1 ? "loan" : "loans"} in Addy today.
            </>
          ) : null}{" "}
          {isSelectedOnly ? (
            "Every other LendingPad loan is skipped, and the ones already in Addy are hidden from this organization. Loans from other systems and loans created in Addy are not affected."
          ) : isEveryLoan ? (
            "Loans of originators who are not in this organization are imported too."
          ) : (
            <>
              Who counts as this organization&rsquo;s people is managed in{" "}
              <button
                type="button"
                className="integrations-inline-link"
                onClick={onOpenPeople}
              >
                People
              </button>
              .
            </>
          )}
        </p>
      </div>

      <section className="integrations-step">
        <header className="integrations-step-head">
          <h4 className="integrations-step-title">Whose loans</h4>
          <p className="integrations-step-lede">
            LendingPad sends the whole company&rsquo;s loans. This decides which
            of them come in, not what anyone can open. That is set per person
            in <b>People</b>.
          </p>
        </header>

        <div className="integrations-choice-list" role="radiogroup" aria-label="Whose loans">
          {availableScopes.map((value) => {
            const copy = SCOPE_COPY[value];
            const isOn = scope === value;
            const isInForce = isScopeChanged && savedScope === value;

            return (
              <label key={value} className={`integrations-choice ${isOn ? "on" : ""}`}>
                <input
                  type="radio"
                  className="integrations-choice-control"
                  name="lendingpad-import-scope"
                  value={value}
                  checked={isOn}
                  onChange={() => setScope(value)}
                  disabled={disabled || savingScope}
                />
                <span className="integrations-choice-body">
                  <span className="integrations-choice-title">
                    {copy?.title || value}
                    {isInForce ? (
                      <span className="integrations-choice-flag">In force</span>
                    ) : null}
                  </span>
                  {copy ? (
                    <span className="integrations-choice-desc">{copy.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>

        {isWidening ? (
          <div className="integrations-save-note warn">
            <span className="integrations-note-mark" aria-hidden="true">
              !
            </span>
            <span>
              Saving imports every existing loan in the selected stages, whoever
              is on it. On a real company account that can be thousands of loans,
              and they become visible to this organization.
            </span>
          </div>
        ) : null}

        {isNarrowing ? (
          <div className="integrations-save-note warn">
            <span className="integrations-note-mark" aria-hidden="true">
              !
            </span>
            <span>
              Saving stops importing loans that have nobody from this organization
              on them, and stops updating the ones already imported. Nothing is
              deleted. Declare the missing people in <b>People</b> first if their
              loans should keep syncing.
            </span>
          </div>
        ) : null}

        {isEnteringSelected ? (
          <div className="integrations-save-note warn">
            <span className="integrations-note-mark" aria-hidden="true">
              !
            </span>
            <span>
              Saving stops importing and updating every LendingPad loan that is not
              selected, and hides the ones already in Addy from this organization.
              Nothing is deleted, and loans from other systems or created in Addy
              are not affected. Selected loans that are not in Addy yet are imported
              now.
              {loans.length === 0
                ? " No loans are selected yet, so no LendingPad loan would show."
                : null}
            </span>
          </div>
        ) : null}

        {isLeavingSelected ? (
          <div className="integrations-save-note warn">
            <span className="integrations-note-mark" aria-hidden="true">
              !
            </span>
            <span>
              Saving shows this organization&rsquo;s other LendingPad loans again
              and imports the ones skipped while only selected loans were imported.
              That can take a few minutes.
            </span>
          </div>
        ) : null}

        <div className="integrations-step-save">
          <span className="integrations-step-save-state">
            {isScopeChanged ? "Unsaved change" : null}
          </span>
          <button
            type="button"
            className={`integrations-btn integrations-btn-primary ${savingScope ? "is-loading" : ""}`}
            onClick={handleSaveScope}
            disabled={disabled || savingScope || !isScopeChanged}
          >
            Save whose loans
          </button>
        </div>
      </section>

      {isSelectedChosen ? (
        <section className="integrations-step">
          <header className="integrations-step-head">
            <h4 className="integrations-step-title">Selected loans</h4>
            <p className="integrations-step-lede">
              {isSelectedOnly
                ? "Only these LendingPad loans are imported, updated and shown to this organization, whatever their stage or team. Loans from other systems and loans created in Addy are not affected."
                : "The LendingPad loans this organization will import and show once you save whose loans. You can prepare the list now."}
            </p>
          </header>

          <form className="integrations-folder-add" onSubmit={handleAddLoan} noValidate>
            <input
              type="text"
              className="integrations-input"
              aria-label="LendingPad loan number"
              placeholder="LendingPad loan numbers, separated by spaces or commas"
              autoComplete="off"
              value={loanNumber}
              onChange={(event) => {
                setLoanNumber(event.target.value);
                setLoanError("");
              }}
              disabled={isLoansDisabled}
            />
            <button
              type="submit"
              className={`integrations-btn integrations-btn-ghost ${savingSelectedLoans ? "is-loading" : ""}`}
              aria-label="Select loan"
              disabled={isLoansDisabled || loanNumber.trim() === ""}
            >
              Add
            </button>
          </form>
          {loanError ? <p className="integrations-field-hint">{loanError}</p> : null}

          {loans.length === 0 ? (
            <div className="integrations-empty">
              {isSelectedOnly
                ? "No loans selected. This organization imports and shows no LendingPad loans."
                : "No loans selected yet."}
            </div>
          ) : (
            <ul className="integrations-folder-list" aria-label="Selected loans">
              {loans.map((loan) => {
                const label = loan.loanNumber || loan.lendingPadLoanId;

                return (
                  <li key={loan.lendingPadLoanId} className="integrations-folder-chip">
                    <span className="integrations-folder-name">{label}</span>
                    {!loan.addyLoanId ? (
                      <span className="integrations-stages-tag add">not imported yet</span>
                  ) : null}
                  <button
                    type="button"
                    className="integrations-chip-btn"
                    aria-label={`Remove ${label}`}
                    onClick={() => onRemoveSelectedLoan(loan.lendingPadLoanId)}
                    disabled={isLoansDisabled}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="integrations-save-note">
          <span className="integrations-note-mark" aria-hidden="true">
            i
          </span>
          <span>
            Adding or removing a loan applies right away.{" "}
            {isSelectedOnly
              ? "A loan that is not in Addy yet is imported when you add it; a removed loan stays in Addy but is hidden and stops updating."
              : "Nothing changes for this organization until you save whose loans."}
          </span>
        </div>

        <div className="integrations-step-save">
          <span className="integrations-step-save-state">
            {loans.length} {loans.length === 1 ? "loan" : "loans"} selected
          </span>
        </div>
      </section>
      ) : null}

      <section className="integrations-step">
        <header className="integrations-step-head">
          <h4 className="integrations-step-title">Which stages</h4>
          <p className="integrations-step-lede">
            Enabling a stage imports its existing loans once; disabling one only
            stops new imports; loans already in Addy are kept.
          </p>
        </header>

        {!syncStages ? (
          <div className="integrations-empty">Stage catalog unavailable.</div>
        ) : (
          <>
            <div className="integrations-stagegroups">
              {groups.map((group) => {
                const ids = group.stages.map((stage) => stage.id);
                const onCount = ids.filter((id) => selected.has(id)).length;
                const isAllOn = onCount === ids.length;
                const isAllOff = onCount === 0;
                const isOpen = openGroups.has(group.title);

                return (
                  <div
                    key={group.title}
                    className={`integrations-stagegroup ${isOpen ? "open" : ""}`}
                  >
                    <div className="integrations-stagegroup-head">
                      <button
                        type="button"
                        className="integrations-stagegroup-toggle"
                        aria-expanded={isOpen}
                        onClick={() => toggleGroup(group.title)}
                      >
                        <span className="integrations-stagegroup-chevron" aria-hidden="true">
                          ›
                        </span>
                        <span className="integrations-stagegroup-title">{group.title}</span>
                        <span className="integrations-stagegroup-bar" aria-hidden="true">
                          {ids.map((id) => (
                            <i key={id} className={selected.has(id) ? "on" : ""} />
                          ))}
                        </span>
                        <span className="integrations-stagegroup-count">
                          {onCount} of {ids.length}
                        </span>
                      </button>
                    </div>

                    {isOpen ? (
                      <div className="integrations-stagegroup-body">
                        <span className="integrations-stages-group-actions">
                          <button
                            type="button"
                            className="integrations-chip-btn"
                            onClick={() => setGroupSelection(ids, true)}
                            disabled={disabled || savingStages || isAllOn}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className="integrations-chip-btn"
                            onClick={() => setGroupSelection(ids, false)}
                            disabled={disabled || savingStages || isAllOff}
                          >
                            None
                          </button>
                        </span>
                        {group.stages.map((stage) => {
                          const isOn = selected.has(stage.id);
                          const willImportExisting = isOn && !savedSet.has(stage.id);
                          const willStopImporting = !isOn && savedSet.has(stage.id);

                          return (
                            <label
                              key={stage.id}
                              className={`integrations-stages-row ${isOn ? "on" : ""}`}
                            >
                              <span className="integrations-stages-id">{stage.id}</span>
                              <span className="integrations-stages-main">
                                <span className="integrations-stages-name">{stage.name}</span>
                                {willImportExisting || willStopImporting ? (
                                  <span className="integrations-stages-tags">
                                    {willImportExisting ? (
                                      <span className="integrations-stages-tag add">
                                        will import existing loans
                                      </span>
                                    ) : null}
                                    {willStopImporting ? (
                                      <span className="integrations-stages-tag stop">
                                        stops importing new loans
                                      </span>
                                    ) : null}
                                  </span>
                                ) : null}
                              </span>
                              <input
                                type="checkbox"
                                className="integrations-stages-check"
                                checked={isOn}
                                onChange={() => toggleStage(stage.id)}
                                disabled={disabled || savingStages}
                              />
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="integrations-step-save">
              <span className="integrations-step-save-state">
                {addedCount > 0 ? <span>+{addedCount} enabled (backfill)</span> : null}
                {addedCount > 0 && removedCount > 0 ? <span> · </span> : null}
                {removedCount > 0 ? <span>-{removedCount} disabled</span> : null}
              </span>
              <span className="integrations-step-save-actions">
                <button
                  type="button"
                  className="integrations-btn integrations-btn-ghost integrations-btn-small"
                  title="Replace the current selection with Addy's suggested starting set"
                  onClick={() => setSelected(new Set(defaultStatusIds))}
                  disabled={disabled || savingStages || isEqualToDefault}
                >
                  Use suggested set
                </button>
                <button
                  type="button"
                  className={`integrations-btn integrations-btn-primary ${savingStages ? "is-loading" : ""}`}
                  onClick={handleSaveStages}
                  disabled={
                    disabled || savingStages || !isStagesChanged || selected.size === 0
                  }
                >
                  Save stages
                </button>
              </span>
            </div>
          </>
        )}
      </section>

      <section className="integrations-step">
        <header className="integrations-step-head">
          <h4 className="integrations-step-title">Which folders to ignore</h4>
          <p className="integrations-step-lede">
            New documents in these LendingPad folders are not imported or processed.
            They stay in LendingPad, and documents already in Addy are not changed.
          </p>
        </header>

        <form className="integrations-folder-add" onSubmit={handleAddFolder} noValidate>
          <input
            type="text"
            className="integrations-input"
            aria-label="Folder name"
            placeholder="Folder name exactly as in LendingPad"
            autoComplete="off"
            maxLength={MAX_FOLDER_NAME_LENGTH}
            value={folderName}
            onChange={(event) => {
              setFolderName(event.target.value);
              setFolderError("");
            }}
            disabled={isFoldersDisabled}
          />
          <button
            type="submit"
            className="integrations-btn integrations-btn-ghost"
            aria-label="Add folder"
            disabled={isFoldersDisabled || folderName.trim() === ""}
          >
            Add
          </button>
        </form>
        {folderError ? <p className="integrations-field-hint">{folderError}</p> : null}

        {folders.length === 0 ? (
          <div className="integrations-empty">
            No folders ignored. Every LendingPad folder is imported.
          </div>
        ) : (
          <ul className="integrations-folder-list" aria-label="Ignored folders">
            {folders.map((name) => (
              <li key={name} className="integrations-folder-chip">
                <span className="integrations-folder-name">{name}</span>
                {!savedFolders.includes(name) ? (
                  <span className="integrations-stages-tag add">not saved</span>
                ) : null}
                {name !== name.trim() ? (
                  <span className="integrations-stages-tag stop">
                    starts or ends with a space
                  </span>
                ) : null}
                <button
                  type="button"
                  className="integrations-chip-btn"
                  aria-label={`Stop ignoring ${name}`}
                  onClick={() => setFolders((prev) => prev.filter((folder) => folder !== name))}
                  disabled={isFoldersDisabled}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="integrations-save-note">
          <span className="integrations-note-mark" aria-hidden="true">
            i
          </span>
          <span>
            Names must match LendingPad exactly, including capital letters and spaces.
            A folder whose name differs in any way keeps importing.
          </span>
        </div>

        <div className="integrations-step-save">
          <span className="integrations-step-save-state">
            {isFoldersChanged ? "Unsaved change" : null}
          </span>
          <button
            type="button"
            className={`integrations-btn integrations-btn-primary ${savingFolders ? "is-loading" : ""}`}
            onClick={handleSaveFolders}
            disabled={isFoldersDisabled || !isFoldersChanged}
          >
            Save ignored folders
          </button>
        </div>
      </section>
    </div>
  );
}
